/**
 * DepthChart - Cumulative depth visualization using Recharts.
 * Shows the total volume available at each price level.
 */

import { memo, useMemo } from 'react'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts'
import { cn } from '../../lib/utils'
import type { OrderBook, Order } from '../../engine/types'

export interface DepthChartProps {
  /** The order book to visualize */
  orderBook: OrderBook
  /** Chart width (use 'auto' for responsive) */
  width?: number | 'auto'
  /** Chart height in pixels */
  height?: number
  /** Whether to show the spread region */
  showSpread?: boolean
  /** Optional className for the container */
  className?: string
}

/** Data point for the depth chart */
interface DepthPoint {
  price: number
  priceDisplay: number
  bidDepth: number | null
  askDepth: number | null
}

/**
 * Calculates cumulative depth from orders.
 */
function calculateCumulativeDepth(
  orders: Order[],
  ascending: boolean
): Map<number, number> {
  // Aggregate by price
  const levels = new Map<number, number>()
  for (const order of orders) {
    const remaining = order.quantity - order.filledQuantity
    if (remaining > 0) {
      levels.set(order.price, (levels.get(order.price) ?? 0) + remaining)
    }
  }

  // Sort prices
  const prices = Array.from(levels.keys()).sort((a, b) =>
    ascending ? a - b : b - a
  )

  // Calculate cumulative
  const cumulative = new Map<number, number>()
  let total = 0
  for (const price of prices) {
    total += levels.get(price) ?? 0
    cumulative.set(price, total)
  }

  return cumulative
}

/**
 * Custom tooltip component for the depth chart.
 */
function DepthTooltip({
  active,
  payload,
}: {
  active?: boolean
  payload?: Array<{ payload: DepthPoint }>
}) {
  if (!active || !payload?.[0]) return null

  const data = payload[0].payload
  const isBid = data.bidDepth !== null

  return (
    <div className="rounded-md border border-border bg-popover px-3 py-2 shadow-md">
      <p className="text-sm font-medium text-foreground">
        Price: {data.priceDisplay}¢
      </p>
      <p className={cn('text-sm', isBid ? 'text-green-600' : 'text-red-600')}>
        {isBid ? 'Bid' : 'Ask'} Depth: {data.bidDepth ?? data.askDepth} shares
      </p>
    </div>
  )
}

/**
 * DepthChart displays cumulative order book depth.
 */
export const DepthChart = memo(
  function DepthChart({
    orderBook,
    width = 'auto',
    height = 200,
    showSpread = true,
    className,
  }: DepthChartProps) {
    // Calculate cumulative depths
    const chartData = useMemo(() => {
      const bidDepth = calculateCumulativeDepth(orderBook.bids, false) // Descending
      const askDepth = calculateCumulativeDepth(orderBook.asks, true) // Ascending

    const bidPrices = Array.from(bidDepth.keys()).sort((a, b) => a - b)
    const askPrices = Array.from(askDepth.keys()).sort((a, b) => a - b)

    const data: DepthPoint[] = []

    // Add bid points (from low to high price)
    for (const price of bidPrices) {
      data.push({
        price,
        priceDisplay: Math.round(price * 100),
        bidDepth: bidDepth.get(price) ?? null,
        askDepth: null,
      })
    }

    // Add ask points (from low to high price)
    for (const price of askPrices) {
      data.push({
        price,
        priceDisplay: Math.round(price * 100),
        bidDepth: null,
        askDepth: askDepth.get(price) ?? null,
      })
    }

    // Sort by price
    data.sort((a, b) => a.price - b.price)

    return data
  }, [orderBook.bids, orderBook.asks])

  // Get spread boundaries
  const { bestBid, bestAsk } = useMemo(() => {
    const sortedBids = orderBook.bids
      .filter((o) => o.quantity > o.filledQuantity)
      .map((o) => o.price)
      .sort((a, b) => b - a)
    const sortedAsks = orderBook.asks
      .filter((o) => o.quantity > o.filledQuantity)
      .map((o) => o.price)
      .sort((a, b) => a - b)

    return {
      bestBid: sortedBids[0] ?? null,
      bestAsk: sortedAsks[0] ?? null,
    }
  }, [orderBook.bids, orderBook.asks])

  const hasData = chartData.length > 0

  if (!hasData) {
    return (
      <div
        className={cn(
          'flex items-center justify-center rounded-lg border border-border bg-card p-4',
          className
        )}
        style={{ height }}
      >
        <p className="text-sm text-muted-foreground">
          No orders to display. Place an order to see the depth chart.
        </p>
      </div>
    )
  }

  const chart = (
    <AreaChart
      data={chartData}
      margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
    >
      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
      <XAxis
        dataKey="priceDisplay"
        type="number"
        domain={['dataMin', 'dataMax']}
        tickFormatter={(value: number) => `${value}¢`}
        className="text-xs"
        tick={{ fill: 'hsl(var(--muted-foreground))' }}
      />
      <YAxis
        className="text-xs"
        tick={{ fill: 'hsl(var(--muted-foreground))' }}
        tickFormatter={(value: number) => value.toString()}
      />
      <Tooltip content={<DepthTooltip />} />

      {/* Bid area (green) */}
      <Area
        type="stepAfter"
        dataKey="bidDepth"
        stroke="hsl(142 76% 36%)"
        fill="hsl(142 76% 36% / 0.3)"
        strokeWidth={2}
        connectNulls={false}
        isAnimationActive={false}
      />

      {/* Ask area (red) */}
      <Area
        type="stepBefore"
        dataKey="askDepth"
        stroke="hsl(0 84% 60%)"
        fill="hsl(0 84% 60% / 0.3)"
        strokeWidth={2}
        connectNulls={false}
        isAnimationActive={false}
      />

      {/* Spread indicators */}
      {showSpread && bestBid !== null && (
        <ReferenceLine
          x={Math.round(bestBid * 100)}
          stroke="hsl(142 76% 36%)"
          strokeDasharray="5 5"
          label={{
            value: 'Best Bid',
            position: 'insideTopLeft',
            fill: 'hsl(142 76% 36%)',
            fontSize: 10,
          }}
        />
      )}
      {showSpread && bestAsk !== null && (
        <ReferenceLine
          x={Math.round(bestAsk * 100)}
          stroke="hsl(0 84% 60%)"
          strokeDasharray="5 5"
          label={{
            value: 'Best Ask',
            position: 'insideTopRight',
            fill: 'hsl(0 84% 60%)',
            fontSize: 10,
          }}
        />
      )}
    </AreaChart>
  )

  return (
    <div
      className={cn('rounded-lg border border-border bg-card p-4', className)}
      role="img"
      aria-label="Order book depth chart"
    >
      <h3 className="mb-3 text-sm font-medium text-foreground">
        Depth Chart
      </h3>

      {width === 'auto' ? (
        <ResponsiveContainer width="100%" height={height}>
          {chart}
        </ResponsiveContainer>
      ) : (
        <div style={{ width, height }}>{chart}</div>
      )}

      {/* Legend */}
      <div className="mt-3 flex justify-center gap-4 text-xs">
        <div className="flex items-center gap-1">
          <div className="h-3 w-3 rounded-full bg-green-500/80" />
          <span className="text-muted-foreground">Buy orders</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="h-3 w-3 rounded-full bg-red-500/80" />
          <span className="text-muted-foreground">Sell orders</span>
        </div>
      </div>
    </div>
  )
  },
  // Custom comparison to detect orderBook changes by comparing array lengths and references
  (prevProps, nextProps) => {
    if (prevProps.orderBook === nextProps.orderBook) return true
    if (prevProps.orderBook.bids !== nextProps.orderBook.bids) return false
    if (prevProps.orderBook.asks !== nextProps.orderBook.asks) return false
    if (prevProps.width !== nextProps.width) return false
    if (prevProps.height !== nextProps.height) return false
    if (prevProps.showSpread !== nextProps.showSpread) return false
    if (prevProps.className !== nextProps.className) return false
    return true
  }
)
